import { useAuth } from "@/hooks/useAuth";
import AccountLayout from "@/components/feature/AccountLayout";
import { mockOrders } from "@/mocks/orders";
import { mockWishlist } from "@/mocks/wishlist";
import { statusLabelsEn, statusLabelsAr } from "@/mocks/orders";

export default function AccountDashboard() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { user } = useAuth();

  const activeOrders = mockOrders.filter((o) => o.status !== "cancelled");
  const totalSpent = mockOrders
    .filter((o) => o.status !== "cancelled")
    .reduce((sum, o) => sum + o.total, 0);
  const recentOrders = [...mockOrders]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  const getStatusLabel = (status: string) =>
    isAr ? statusLabelsAr[status as keyof typeof statusLabelsAr] : statusLabelsEn[status as keyof typeof statusLabelsEn];

  const statusStyles: Record<string, string> = {
    delivered: "bg-secondary-100 text-secondary-700",
    shipped: "bg-accent-100 text-accent-700",
    processing: "bg-primary-100 text-primary-700",
    cancelled: "bg-foreground-100 text-foreground-500",
  };

  return (
    <AccountLayout activeTab="dashboard">
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-lg text-primary-600"></i>
            </div>
            <span className="text-sm text-foreground-500">{t("account.total_orders")}</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{activeOrders.length}</p>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-lg text-accent-600"></i>
            </div>
            <span className="text-sm text-foreground-500">{t("account.total_spent")}</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">${totalSpent.toFixed(2)}</p>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-heart-line text-lg text-secondary-600"></i>
            </div>
            <span className="text-sm text-foreground-500">{t("account.wishlist_count")}</span>
          </div>
          <p className="text-2xl font-bold text-foreground-950">{mockWishlist.length}</p>
        </div>
      </div>

      {/* Recent orders */}
      <div>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-heading font-bold text-foreground-950">
            {t("account.recent_orders")}
          </h2>
          <Link
            to="/account/orders"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer flex items-center gap-1"
          >
            {t("account.view_all_orders")}
            <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
          </Link>
        </div>

        {recentOrders.length > 0 ? (
          <div className="space-y-3">
            {recentOrders.map((order) => (
              <Link
                key={order.id}
                to={`/account/orders/${order.id}`}
                className="block bg-background-50 border border-background-200 rounded-xl p-5 hover:border-background-300 transition-colors cursor-pointer"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-4">
                    {/* Product thumbnails */}
                    <div className="flex -space-x-2">
                      {order.items.slice(0, 3).map((item, i) => (
                        <div
                          key={i}
                          className="w-10 h-10 rounded-lg border-2 border-background-50 bg-background-100 overflow-hidden"
                        >
                          <img
                            src={item.imageUrl}
                            alt={isAr ? item.nameAr : item.nameEn}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ))}
                      {order.items.length > 3 && (
                        <div className="w-10 h-10 rounded-lg border-2 border-background-50 bg-background-200 flex items-center justify-center">
                          <span className="text-xs font-medium text-foreground-500">
                            +{order.items.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">
                        {t("account.order_number")} #{order.orderNumber}
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {order.items.length}{" "}
                        {order.items.length === 1 ? t("account.item") : t("account.items")}
                        {" · "}
                        {new Date(order.date).toLocaleDateString(
                          isAr ? "ar-EG" : "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[order.status]}`}
                    >
                      {getStatusLabel(order.status)}
                    </span>
                    <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                      ${order.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-background-50 border border-background-200 rounded-xl">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-2xl text-foreground-400"></i>
            </div>
            <h3 className="text-lg font-medium text-foreground-900 mb-2">{t("account.no_orders")}</h3>
            <p className="text-sm text-foreground-500 mb-6 max-w-sm mx-auto">{t("account.no_orders_desc")}</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              {t("account.start_shopping")}
            </Link>
          </div>
        )}
      </div>
    </AccountLayout>
  );
}